import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export type BusinessMode = "facility" | "digital";

const STORAGE_KEY = "managematch:business-mode";
const PARAM_KEY = "modus";
const PARAM_TO_MODE: Record<string, BusinessMode> = {
  gebaeude: "facility",
  digital: "digital",
};
const MODE_TO_PARAM: Record<BusinessMode, string> = {
  facility: "gebaeude",
  digital: "digital",
};

interface BusinessModeContextValue {
  mode: BusinessMode;
  setMode: (mode: BusinessMode) => void;
  toggleMode: () => void;
}

const BusinessModeContext = createContext<BusinessModeContextValue | undefined>(undefined);

const readInitialMode = (paramValue: string | null): BusinessMode => {
  if (paramValue && PARAM_TO_MODE[paramValue]) return PARAM_TO_MODE[paramValue];
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "facility" || stored === "digital") return stored;
  }
  return "facility";
};

export const BusinessModeProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setModeState] = useState<BusinessMode>(() =>
    readInitialMode(searchParams.get(PARAM_KEY))
  );

  // Re-sync when URL param changes (e.g. external link)
  useEffect(() => {
    const paramValue = searchParams.get(PARAM_KEY);
    if (paramValue && PARAM_TO_MODE[paramValue] && PARAM_TO_MODE[paramValue] !== mode) {
      setModeState(PARAM_TO_MODE[paramValue]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Persist to localStorage
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const setMode = useCallback(
    (next: BusinessMode) => {
      setModeState(next);
      const params = new URLSearchParams(searchParams);
      params.set(PARAM_KEY, MODE_TO_PARAM[next]);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const toggleMode = useCallback(() => {
    setMode(mode === "facility" ? "digital" : "facility");
  }, [mode, setMode]);

  return (
    <BusinessModeContext.Provider value={{ mode, setMode, toggleMode }}>
      {children}
    </BusinessModeContext.Provider>
  );
};

export const useBusinessMode = () => {
  const ctx = useContext(BusinessModeContext);
  if (!ctx) throw new Error("useBusinessMode must be used inside BusinessModeProvider");
  return ctx;
};

// Static content map — reused by Hero, SEO, CTA, etc.
export const businessModeContent: Record<
  BusinessMode,
  {
    label: string;
    shortLabel: string;
    heroHeadline: string;
    heroHeadlineAccent: string;
    heroSubline: string;
    ctaLabel: string;
    seoTitle: string;
    seoDescription: string;
    serviceSlugs: string[];
    sectionTitle: string;
    sectionSubtitle: string;
  }
> = {
  facility: {
    label: "Gebäude & Service",
    shortLabel: "Gebäude",
    heroHeadline: "Professionelle Dienstleistungen",
    heroHeadlineAccent: "koordiniert in Hanau",
    heroSubline:
      "ManageMatch koordiniert qualifizierte Fachkräfte für Privat- und Gewerbekunden: Hausmeister, Reinigung, Elektro & mehr aus einer Hand.",
    ctaLabel: "Jetzt Angebot anfordern",
    seoTitle: "ManageMatch – Dienstleistungen in Hanau & Rhein-Main",
    seoDescription:
      "ManageMatch koordiniert Hausmeister, Reinigung, Elektro, Garten, Winterdienst, Security & IT in Hanau und Rhein-Main. Jetzt kostenlos anfragen!",
    serviceSlugs: [
      "hausmeisterservice",
      "reinigungsservice",
      "winterdienst",
      "gartenarbeiten",
      "elektro-service",
      "security-service",
      "car-detailing",
    ],
    sectionTitle: "Unsere Dienstleistungen in Hanau & Rhein-Main",
    sectionSubtitle:
      "ManageMatch koordiniert qualifizierte Fachkräfte für Hausmeisterservice, Reinigung, Elektro, Gartenpflege und mehr – alles aus einer Hand.",
  },
  digital: {
    label: "Digital & IT",
    shortLabel: "Digital",
    heroHeadline: "Digitale Lösungen",
    heroHeadlineAccent: "remote & deutschlandweit",
    heroSubline:
      "ManageMatch koordiniert Websites, IT-Helpdesk und technischen Support – professionell umgesetzt, remote betreut und deutschlandweit verfügbar.",
    ctaLabel: "Digitales Projekt anfragen",
    seoTitle: "ManageMatch Digital & IT – Website, Helpdesk & Support",
    seoDescription:
      "Website-Erstellung ab 449 €, IT Helpdesk & technischer Support – remote und deutschlandweit von ManageMatch koordiniert. Jetzt Projekt anfragen!",
    serviceSlugs: ["website-erstellung", "it-helpdesk"],
    sectionTitle: "Digitale Leistungen – remote & deutschlandweit",
    sectionSubtitle:
      "Von der Website bis zum IT-Support: ManageMatch koordiniert digitale Fachkräfte für Ihr Projekt – schnell, transparent und remote.",
  },
};
